import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { ShareBar, Button } from 'components/elements'
import { useSession } from 'context/Session'
import { format } from 'date-fns'
import Linkify from 'linkify-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import * as R from 'ramda'
import { useState } from 'react'
import slugfy from 'slug'

import * as S from './AskMeDetail.styles'
import Questions from './Questions'
import { completeAMQ, deleteAMQ } from '../modules/datasource'

const defaultProps = {}
const propTypes = {}

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL

const dialogMap = {
  delete: {
    title: 'Delete Ask Me Questions',
    text: 'Are you sure you want to delete this ask me questions?',
  },
  complete: {
    title: 'Complete Ask Me Questions',
    text: 'Are you sure you want to mark this ask me questions as complete?',
    confirmLabel: 'Complete',
    confirmColor: 'primary',
  },
}

export const AskMeDetail = ({
  questionId,
  data,
  questionsData,
  accessToken,
}) => {
  const router = useRouter()
  const {
    profile,
    publishedAt,
    // statement,
    // projectDetails
    text,
    title,
  } = data
  const [crrDialog, setCrrDialog] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const { user, verifyPermission } = useSession()
  const isCompleted = !!data?.completed
  const [myVote, setMyVote] = useState(data?.voted)
  const [crrVotes, setCrrVotes] = useState(data?.votes || 0)
  const isStaff = verifyPermission(['Admin', 'Staff', 'SuperAdmin'])
  const slug = slugfy(title)

  const isMyAMQ = R.equals(user?.profile?.username, data?.profile?.username)

  const handleClose = () => !isFetching && setShowDialog(false)

  const handleClick = async () => {
    setIsFetching(true)
    if (crrDialog === 'complete') {
      await completeAMQ({ questionId, accessToken })
      setIsFetching(false)
      handleClose()
      router.reload()
    } else {
      await deleteAMQ({ questionId, accessToken })
      router.push('/questions')
    }
  }

  const handleOpen = d => {
    setCrrDialog(d)
    setShowDialog(true)
  }

  const voteConfig = {
    accessToken,
    id: data?.id,
    setCrrVotes,
    crrVotes,
    myVote,
    setMyVote,
    isCompleted,
  }

  return (
    <>
      <S.Container>
        <S.Content>
          <S.AMQFooter>
            <S.Breadcrumb>
              <S.StyledLink href="/questions">All Questions</S.StyledLink>
              &nbsp;&nbsp;/
              {data?.groupId && (
                <>
                  &nbsp;&nbsp;
                  <S.StyledLink href={`/groups/${data?.groupId}`}>
                    Group
                  </S.StyledLink>
                  &nbsp;&nbsp;/
                </>
              )}
              <Link href={`/p/${profile?.username}`} passHref>
                <S.AuthorImg>
                  {profile?.profileImage ? (
                    <S.StyledImg src={profile?.profileImage} />
                  ) : (
                    R.head(profile?.username)
                  )}
                </S.AuthorImg>
              </Link>
              {/* <S.AMQBadge completed={isCompleted}>
              {isCompleted ? 'COMPLETED' : 'ACTIVE'}
            </S.AMQBadge> */}
              <S.PublishDate>
                {format(new Date(publishedAt), 'LLLL d, yyyy')}
              </S.PublishDate>
            </S.Breadcrumb>
            <ShareBar
              url={`${SITE_URL}/questions/${questionId}/${slug}`}
              actions={
                (isMyAMQ || isStaff) && !isCompleted
                  ? [
                      {
                        label: 'Delete',
                        onClick: () => handleOpen('delete'),
                      },
                    ]
                  : []
              }
            />
          </S.AMQFooter>
          <S.QuestionContent>
            <S.Votes {...voteConfig} />
            <S.QuestionContent style={{ flexDirection: 'column' }}>
              <S.AMQTitle>{title}</S.AMQTitle>
              <S.AMQDescription>
                <Linkify options={{ target: '_blank' }}>{text}</Linkify>
              </S.AMQDescription>
            </S.QuestionContent>
          </S.QuestionContent>
          {!R.isEmpty(data?.tags || []) && (
            <S.TagsContainer>
              {data?.tags.map(tag => (
                <Link
                  href={`/topics/${tag?.slug}`}
                  key={`tag-${tag.name}`}
                  passHref
                >
                  <S.TagItem label={tag.name} />
                </Link>
              ))}
            </S.TagsContainer>
          )}
          {!R.isEmpty(data?.references || []) && (
            <S.ReferenceTitle>References</S.ReferenceTitle>
          )}
          {!R.isEmpty(data?.references || []) && (
            <S.RefList>
              {data?.references.map(ref => (
                <S.ListItem key={ref.title}>
                  {ref?.type === 'Link' ? <S.LinkIcon /> : <S.PdfIcon />}
                  <S.ItemDesc
                    href={ref?.value}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {ref?.title || ref?.value}
                  </S.ItemDesc>
                </S.ListItem>
              ))}
            </S.RefList>
          )}
        </S.Content>
      </S.Container>

      <S.QuestionsContainer>
        <S.Content>
          <Questions
            ket={isMyAMQ}
            questionId={questionId}
            data={data}
            initialState={questionsData}
            accessToken={accessToken}
            isMyAMQ={isMyAMQ}
            isCompleted={isCompleted}
            isStaff={isStaff}
          />
        </S.Content>
      </S.QuestionsContainer>

      <Dialog
        open={showDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {dialogMap[crrDialog]?.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogMap[crrDialog]?.text}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={isFetching} variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            loading={isFetching}
            color={dialogMap[crrDialog]?.confirmColor || 'error'}
            variant="text"
            onClick={handleClick}
            autoFocus
          >
            {dialogMap[crrDialog]?.confirmLabel || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

AskMeDetail.defaultProps = defaultProps
AskMeDetail.propTypes = propTypes

export default AskMeDetail
